import { EditorView, Decoration, ViewPlugin } from '@codemirror/view';
import { StateField, StateEffect } from '@codemirror/state';
import { handleDependencySuggestionClick } from './claimDependencySuggestions.js';
import { getCommentLineRanges, overlapsCommentRange } from './commentUtils.js';

/**
 * CodeMirror extension for highlighting claim dependencies and showing suggestions
 * 
 * This extension:
 * 1. Parses claim dependency text (e.g., "The method of claim 1")
 * 2. Compares with backend-computed dependencies
 * 3. Highlights outdated or missing dependencies
 * 4. Shows tooltips with suggested dependencies
 */

// State effect for updating dependencies from backend
const setDependencies = StateEffect.define();

// State effect for setting analysis failed state
const setAnalysisFailed = StateEffect.define();

// State field to store claim dependencies and analysis state
const dependenciesState = StateField.define({
    create() {
        return {
            dependencies: {},
            analysisFailed: false
        };
    },
    update(value, tr) {
        for (let effect of tr.effects) {
            if (effect.is(setDependencies)) {
                console.log('[DependenciesState] Setting dependencies:', effect.value);
                return {
                    dependencies: effect.value,
                    analysisFailed: false  // Clear error state when dependencies are set
                };
            }
            if (effect.is(setAnalysisFailed)) {
                return {
                    dependencies: {},  // Clear dependencies when analysis fails
                    analysisFailed: effect.value
                };
            }
        }
        return value;
    }
});

// Helper function to format dependencies
function formatDependencies(deps) {
    if (!deps || deps.length === 0) return '';
    if (deps.length === 1) return deps[0].toString();
    
    // Format as range if consecutive
    const sorted = [...deps].sort((a, b) => a - b);
    const isConsecutive = sorted.every((num, i) => 
        i === 0 || num === sorted[i - 1] + 1
    );
    
    if (isConsecutive && sorted.length > 2) {
        return `${sorted[0]}-${sorted[sorted.length - 1]}`;
    }
    
    // Format as simple comma-separated list
    return sorted.join(', ');
}

// Function to parse claim dependencies from text
function parseClaimDependencies(text, commentRanges = []) {
    const dependencies = new Map();
    
    // First, let's extract individual claims to debug
    const claimTexts = new Map();
    // Updated pattern to handle indentation before claim numbers
    const claimPattern = /^\s*(\d+)\.\s+([^]*?)(?=^\s*\d+\.|$)/gm;
    let claimMatch;
    while ((claimMatch = claimPattern.exec(text)) !== null) {
        const claimNum = parseInt(claimMatch[1]);
        const claimText = claimMatch[2].trim();
        // Skip if this claim number is in a comment
        if (!overlapsCommentRange(claimMatch.index, claimMatch.index + claimMatch[0].length, commentRanges)) {
            claimTexts.set(claimNum, claimText);
        }
    }

    // More specific patterns that capture the exact claim reference portion
    // IMPORTANT: More specific patterns must come BEFORE less specific ones
    // e.g., "claims X or Y" must come before "claim X" to avoid partial matches
    const patterns = [
        {
            // "The method of claim X or claim Y" or "The method of claims X or claims Y"
            regex: /(\d+)\.\s+(The\s+(?:method|system|device|apparatus|process|composition|article)\s+of\s+)(claims?\s+\d+\s+or\s+claims?\s+\d+)/gi,
            type: 'or_list_full'
        },
        {
            // "The method of claim X or Y" or "The method of claims X or Y"
            regex: /(\d+)\.\s+(The\s+(?:method|system|device|apparatus|process|composition|article)\s+of\s+)(claims?\s+\d+\s+or\s+\d+)/gi,
            type: 'or_list'
        },
        {
            // "The method of claims X-Y" (direct range without "any of")
            regex: /(\d+)\.\s+(The\s+(?:method|system|device|apparatus|process|composition|article)\s+of\s+)(claims\s+\d+-\d+)/gi,
            type: 'range'
        },
        {
            // "The method of claim X" (single claim - must come AFTER multi-claim patterns)
            regex: /(\d+)\.\s+(The\s+(?:method|system|device|apparatus|process|composition|article)\s+of\s+)(claims?\s+\d+)/gi,
            type: 'single'
        },
        {
            // "The method of any of claims X-Y"
            regex: /(\d+)\.\s+(The\s+(?:method|system|device|apparatus|process|composition|article)\s+of\s+any\s+of\s+)(claims\s+\d+-\d+)/gi,
            type: 'range'
        },
        {
            // "The method of claims X and Y"
            regex: /(\d+)\.\s+(The\s+(?:method|system|device|apparatus|process|composition|article)\s+of\s+)(claims\s+\d+\s+and\s+\d+)/gi,
            type: 'list'
        },
        {
            // "The method according to claim X"
            regex: /(\d+)\.\s+(The\s+(?:method|system|device|apparatus|process|composition|article)\s+according\s+to\s+)(claim\s+\d+)/gi,
            type: 'single'
        },
        {
            // "The method of any preceding claim"
            regex: /(\d+)\.\s+(The\s+(?:method|system|device|apparatus|process|composition|article)\s+of\s+)(any\s+preceding\s+claim)/gi,
            type: 'preceding'
        },
        {
            // "A method according to any one of claims X to Y"
            regex: /(\d+)\.\s+((?:The|A)\s+(?:method|system|device|apparatus|process|composition|article)\s+according\s+to\s+any\s+one\s+of\s+)(claims\s+\d+\s+to\s+\d+)/gi,
            type: 'range_to'
        },
        {
            // "The method of any one of claims X-Y"
            regex: /(\d+)\.\s+(The\s+(?:method|system|device|apparatus|process|composition|article)\s+of\s+any\s+one\s+of\s+)(claims\s+\d+-\d+)/gi,
            type: 'range'
        }
    ];
    
    patterns.forEach(({ regex, type }) => {
        let match;
        regex.lastIndex = 0; // Reset regex
        
        while ((match = regex.exec(text)) !== null) {
            // Skip if this match is in a comment
            if (overlapsCommentRange(match.index, match.index + match[0].length, commentRanges)) {
                continue;
            }
            
            const claimNum = parseInt(match[1]);
            const prefix = match[2];
            const claimRefText = match[3];
            let deps = [];
            
            // Extract dependencies based on type
            if (type === 'single') {
                // For single, extract number from "claim X" or "claims X"
                const singleMatch = /claims?\s+(\d+)/i.exec(claimRefText);
                if (singleMatch) {
                    const dep = parseInt(singleMatch[1]);
                    deps.push(dep);  // Don't filter - report what's written
                }
            } else if (type === 'or_list') {
                // Handle "claim X or Y" or "claims X or Y" format
                const orMatch = /claims?\s+(\d+)\s+or\s+(\d+)/i.exec(claimRefText);
                if (orMatch) {
                    // Don't filter - report exactly what's written
                    deps = [parseInt(orMatch[1]), parseInt(orMatch[2])];
                }
            } else if (type === 'or_list_full') {
                // Handle "claim X or claim Y" or "claims X or claims Y" format
                const orMatch = /claims?\s+(\d+)\s+or\s+claims?\s+(\d+)/i.exec(claimRefText);
                if (orMatch) {
                    // Don't filter - report exactly what's written
                    deps = [parseInt(orMatch[1]), parseInt(orMatch[2])];
                }
            } else if (type === 'range') {
                const rangeMatch = /claims\s+(\d+)-(\d+)/i.exec(claimRefText);
                if (rangeMatch) {
                    const start = parseInt(rangeMatch[1]);
                    const end = parseInt(rangeMatch[2]);
                    // Don't filter - report the full range as written
                    for (let i = start; i <= end; i++) {
                        deps.push(i);
                    }
                }
            } else if (type === 'range_to') {
                const rangeMatch = /claims\s+(\d+)\s+to\s+(\d+)/i.exec(claimRefText);
                if (rangeMatch) {
                    const start = parseInt(rangeMatch[1]);
                    const end = parseInt(rangeMatch[2]);
                    // Don't filter - report the full range as written
                    for (let i = start; i <= end; i++) {
                        deps.push(i);
                    }
                }
            } else if (type === 'list') {
                const listMatch = /claims\s+(\d+)\s+and\s+(\d+)/i.exec(claimRefText);
                if (listMatch) {
                    // Don't filter - report exactly what's written
                    deps = [parseInt(listMatch[1]), parseInt(listMatch[2])];
                }
            } else if (type === 'preceding') {
                // All claims before current - this one still makes sense to filter
                // since "preceding" by definition means "before this one"
                for (let i = 1; i < claimNum; i++) {
                    deps.push(i);
                }
            }
            
            if (deps.length > 0) {
                // Skip if we already have a match for this claim (more specific patterns come first)
                if (dependencies.has(claimNum)) {
                    continue;
                }

                // Calculate the exact position of just the claim reference part
                // Find where the claim reference text actually appears in the original match
                const fullMatch = match[0];
                const claimRefIndex = fullMatch.lastIndexOf(claimRefText);

                if (claimRefIndex !== -1) {
                    const refStart = match.index + claimRefIndex;
                    const refEnd = refStart + claimRefText.length;

                    // Debug log for this specific dependency detection
                    const claimTextExists = claimTexts.has(claimNum);
                    if (!claimTextExists) {
                        console.log(`[ParseClaimDeps] ERROR: Found dependency for claim ${claimNum} but claim text not extracted!`, {
                            type,
                            claimRefText,
                            detectedDeps: deps,
                            fullMatch
                        });
                    }

                    dependencies.set(claimNum, {
                        deps,
                        start: refStart,
                        end: refEnd,
                        text: claimRefText,
                        fullText: fullMatch,
                        prefix: prefix
                    });
                }
            }
        }
    });

    return dependencies;
}

// View plugin for dependency highlighting
const dependencyHighlighter = ViewPlugin.fromClass(class {
    constructor(view) {
        this.decorations = this.computeDecorations(view);
    }

    update(update) {
        if (update.docChanged || update.viewportChanged || 
            update.state.field(dependenciesState) !== update.startState.field(dependenciesState)) {
            this.decorations = this.computeDecorations(update.view);
        }
    }

    computeDecorations(view) {
        const decorations = [];
        const doc = view.state.doc;
        const text = doc.toString();
        const state = view.state.field(dependenciesState);
        const backendDeps = state.dependencies;
        const analysisFailed = state.analysisFailed;
        
        // Get comment ranges to exclude from highlighting
        const commentRanges = getCommentLineRanges(view);
        const parsedDeps = parseClaimDependencies(text, commentRanges);

        // If analysis failed, mark all dependency references with error state
        if (analysisFailed) {
            parsedDeps.forEach((depInfo, claimNum) => {
                // Skip if this decoration would overlap with a comment
                if (!overlapsCommentRange(depInfo.start, depInfo.end, commentRanges)) {
                    decorations.push(
                        Decoration.mark({
                            class: 'claim-dependency-analysis-failed',
                            attributes: {
                                'data-claim': claimNum.toString()
                            }
                        }).range(depInfo.start, depInfo.end)
                    );
                }
            });
            return Decoration.set(decorations.sort((a, b) => a.from - b.from));
        }
        
        // If no backend dependencies at all, show all references with yellow dotted underline
        if (Object.keys(backendDeps).length === 0) {
            parsedDeps.forEach((depInfo, claimNum) => {
                decorations.push(
                    Decoration.mark({
                        class: 'claim-dependency-analysis-failed',
                        attributes: {
                            'data-claim': claimNum.toString()
                        }
                    }).range(depInfo.start, depInfo.end)
                );
            });
            return Decoration.set(decorations.sort((a, b) => a.from - b.from));
        }
        
        // Compare parsed dependencies with backend suggestions
        parsedDeps.forEach((depInfo, claimNum) => {
            const suggestedDeps = backendDeps[claimNum] || [];
            const currentDeps = depInfo.deps;

            // Check if dependencies match
            const depsMatch = JSON.stringify(currentDeps.sort()) === JSON.stringify(suggestedDeps.sort());
            
            if (!depsMatch && suggestedDeps.length > 0) {
                // Dependencies don't match - show orange wavy suggestion
                const from = depInfo.start;
                const to = depInfo.end;
                
                // Skip if this decoration would overlap with a comment
                if (!overlapsCommentRange(from, to, commentRanges)) {
                    // Add underline decoration with hover effect
                    // Note: No 'title' attribute to prevent native browser tooltip
                    decorations.push(
                        Decoration.mark({
                            class: 'claim-dependency-outdated',
                            attributes: {
                                'data-claim': claimNum.toString(),
                                'data-current-deps': JSON.stringify(currentDeps),
                                'data-suggested-deps': JSON.stringify(suggestedDeps)
                                // Removed 'title' to prevent double tooltips
                            }
                        }).range(from, to)
                    );
                }
            } else if (suggestedDeps.length === 0) {
                // No suggestion available - show yellow dotted underline
                if (!overlapsCommentRange(depInfo.start, depInfo.end, commentRanges)) {
                    decorations.push(
                        Decoration.mark({
                            class: 'claim-dependency-analysis-failed',
                            attributes: {
                                'data-claim': claimNum.toString()
                            }
                        }).range(depInfo.start, depInfo.end)
                    );
                }
            }
            // If depsMatch && suggestedDeps.length > 0, then dependencies are correct - no decoration needed
        });
        
        // Check for missing dependencies (claims that should have deps but don't)
        Object.entries(backendDeps).forEach(([claimNum, suggestedDeps]) => {
            if (suggestedDeps.length > 0 && !parsedDeps.has(parseInt(claimNum))) {
                // This claim should have dependencies but doesn't
                // Find the claim in the text
                const claimPattern = new RegExp(`(${claimNum})\\.\\s+`, 'g');
                const match = claimPattern.exec(text);
                if (match) {
                    const from = match.index;
                    const to = match.index + match[0].length;
                    // Skip if this decoration would overlap with a comment
                    if (!overlapsCommentRange(from, to, commentRanges)) {
                        decorations.push(
                            Decoration.mark({
                                class: 'claim-dependency-missing',
                                attributes: {
                                    'data-claim': claimNum,
                                    'data-suggested-deps': JSON.stringify(suggestedDeps)
                                    // Removed 'title' to prevent double tooltips
                                }
                            }).range(from, to)
                        );
                    }
                }
            }
        });
        
        return Decoration.set(decorations.sort((a, b) => a.from - b.from));
    }
}, {
    decorations: v => v.decorations
});

// Styles for dependency highlighting
export const dependencyStyles = EditorView.theme({
    '.cm-content .claim-dependency-outdated': {
        textDecoration: 'underline wavy #ff9800 !important',
        cursor: 'pointer',
        position: 'relative',
        transition: 'background-color 0.2s ease',
        '&:hover': {
            backgroundColor: 'rgba(255, 235, 59, 0.4)',  // Yellow highlight on hover
            borderRadius: '2px'
        }
    },
    '.cm-content .claim-dependency-missing': {
        textDecoration: 'underline dotted #fbbf24 !important',  // Yellow dotted underline like analysis-failed
        cursor: 'help',
        position: 'relative',
        '&:hover': {
            backgroundColor: 'rgba(251, 191, 36, 0.1)',  // Yellow highlight on hover
            borderRadius: '2px'
        }
    },
    '.cm-content .claim-dependency-analysis-failed': {
        textDecoration: 'underline dotted #fbbf24 !important',  // Yellow-400 for analysis not run state
        cursor: 'help',
        position: 'relative',
        '&:hover': {
            backgroundColor: 'rgba(251, 191, 36, 0.1)',  // Yellow highlight on hover
            borderRadius: '2px'
        }
    }
});

// Tooltip layer for showing dependency suggestions on hover
const tooltipLayer = ViewPlugin.fromClass(class {
    constructor(view) {
        this.tooltip = null;
        this.view = view;
        this.handleMouseMove = this.handleMouseMove.bind(this);
        this.handleMouseLeave = this.handleMouseLeave.bind(this);
        
        view.dom.addEventListener('mousemove', this.handleMouseMove);
        view.dom.addEventListener('mouseleave', this.handleMouseLeave);
    }
    
    handleMouseMove(event) {
        // Check if we're hovering over an element with dependency data
        const target = event.target;
        if (!target || !target.classList) {
            this.hideTooltip();
            return;
        }
        
        // Only log if it's a dependency-related class
        const classes = Array.from(target.classList);
        if (classes.some(c => c.includes('dependency'))) {
            console.log('[Tooltip] Mouse over dependency element:', classes.join(', '));
        }
        
        // Check if analysis failed - show error tooltip
        if (target.classList.contains('claim-dependency-analysis-failed')) {
            console.log('[Tooltip] Showing analysis failed tooltip');
            this.showAnalysisFailedTooltip(event);
            return;
        }
        
        // Check if the element has the claim-dependency-outdated class
        if (target.classList.contains('claim-dependency-outdated')) {
            console.log('[Tooltip] Element has outdated class, checking data');
            const data = {
                'data-claim': target.getAttribute('data-claim'),
                'data-current-deps': target.getAttribute('data-current-deps'),
                'data-suggested-deps': target.getAttribute('data-suggested-deps')
            };
            console.log('[Tooltip] Outdated dependency data:', {
                claim: data['data-claim'],
                currentDeps: data['data-current-deps'],
                suggestedDeps: data['data-suggested-deps'],
                hasSuggested: !!data['data-suggested-deps']
            });
            
            if (data['data-suggested-deps']) {
                console.log('[Tooltip] Calling showTooltip');
                this.showTooltip(event, data);
            } else {
                console.log('[Tooltip] No suggested deps, not showing tooltip');
            }
        } else {
            this.hideTooltip();
        }
    }
    
    handleMouseLeave() {
        this.hideTooltip();
    }
    
    showAnalysisFailedTooltip(event) {
        if (!this.tooltip) {
            this.tooltip = document.createElement('div');
            this.tooltip.className = 'cm-dependency-tooltip cm-dependency-tooltip-error';
            document.body.appendChild(this.tooltip);
        } else {
            // Update classes for error state
            this.tooltip.className = 'cm-dependency-tooltip cm-dependency-tooltip-error';
        }
        
        this.tooltip.innerHTML = `
            <div class="tooltip-message">
                <div class="tooltip-error">Term analysis failed to run. Cannot automatically suggest claim dependencies.</div>
            </div>
        `;
        
        // Position tooltip near cursor
        this.tooltip.style.left = event.clientX + 10 + 'px';
        this.tooltip.style.top = event.clientY - 40 + 'px';
        this.tooltip.style.display = 'block';
    }
    
    showTooltip(event, data) {
        if (!this.tooltip) {
            this.tooltip = document.createElement('div');
            this.tooltip.className = 'cm-dependency-tooltip';
            document.body.appendChild(this.tooltip);
        } else {
            // Reset to normal tooltip class
            this.tooltip.className = 'cm-dependency-tooltip';
        }
        
        const currentDeps = JSON.parse(data['data-current-deps']);
        const suggestedDeps = JSON.parse(data['data-suggested-deps']);
        const currentText = formatDependencies(currentDeps) || 'none';
        const suggestedText = formatDependencies(suggestedDeps);
        
        this.tooltip.innerHTML = `
            <div class="tooltip-message">
                <div class="tooltip-line"><span class="tooltip-label">Current claim dependencies:</span> ${currentText}</div>
                <div class="tooltip-line"><span class="tooltip-label">Suggested claim dependencies:</span> ${suggestedText}</div>
                <div class="tooltip-action">Click to accept</div>
            </div>
        `;
        
        // Position tooltip near cursor
        this.tooltip.style.left = event.clientX + 10 + 'px';
        this.tooltip.style.top = event.clientY - 40 + 'px';
        this.tooltip.style.display = 'block';
    }
    
    hideTooltip() {
        if (this.tooltip) {
            this.tooltip.style.display = 'none';
        }
    }
    
    destroy() {
        this.view.dom.removeEventListener('mousemove', this.handleMouseMove);
        this.view.dom.removeEventListener('mouseleave', this.handleMouseLeave);
        if (this.tooltip && this.tooltip.parentNode) {
            this.tooltip.parentNode.removeChild(this.tooltip);
        }
    }
});

// Tooltip styles using the same visual language as article error tooltips
const tooltipStyles = EditorView.theme({
    '.cm-dependency-tooltip': {
        position: 'fixed',
        zIndex: '10000',
        padding: '12px 16px',
        fontSize: '14px',
        lineHeight: '1.4',
        whiteSpace: 'normal',
        maxWidth: '400px',
        minWidth: '300px',
        borderRadius: '6px',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
        pointerEvents: 'none',
        display: 'none',
        // Light mode colors - orange/warning theme for dependency issues
        color: '#b45309',  // Amber-700 for good contrast
        backgroundColor: '#fef3c7',  // Amber-100 background
        border: '1px solid #fbbf24'  // Amber-400 border
    },
    '[data-theme="dark"] .cm-dependency-tooltip': {
        // Dark mode colors - matches article error tooltip
        color: 'var(--color-tooltip-text-error)',  // #ff8b8b
        backgroundColor: 'var(--color-tooltip-bg-subtle)',  // #4D4A43
        border: '1px solid var(--color-tooltip-text-error)',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)'
    },
    '.cm-dependency-tooltip.cm-dependency-tooltip-error': {
        // Analysis not run state colors - yellow/amber theme for light mode
        color: '#92400e',  // Amber-800 for text
        backgroundColor: '#fef3c7',  // Amber-100 background (same as suggestion)
        border: '1px solid #fbbf24'  // Amber-400 border
    },
    '[data-theme="dark"] .cm-dependency-tooltip.cm-dependency-tooltip-error': {
        // Dark mode error state - same as regular dark mode tooltip
        color: 'var(--color-tooltip-text-error)',
        backgroundColor: 'var(--color-tooltip-bg-subtle)',
        border: '1px solid var(--color-tooltip-text-error)',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)'
    },
    '.cm-dependency-tooltip .tooltip-message': {
        color: 'inherit'
    },
    '.cm-dependency-tooltip .tooltip-line': {
        marginBottom: '4px',
        lineHeight: '1.3'
    },
    '.cm-dependency-tooltip .tooltip-label': {
        fontWeight: '600',
        color: '#92400e'  // Darker amber for labels in light mode
    },
    '[data-theme="dark"] .cm-dependency-tooltip .tooltip-label': {
        fontWeight: '600',
        color: 'var(--color-tooltip-text-error)'  // Match dark mode text color
    },
    '.cm-dependency-tooltip .tooltip-action': {
        marginTop: '8px',
        paddingTop: '6px',
        borderTop: '1px solid rgba(251, 191, 36, 0.2)',
        fontStyle: 'italic',
        fontSize: '13px',
        opacity: '0.9',
        color: '#b45309'
    },
    '.cm-dependency-tooltip .tooltip-error': {
        lineHeight: '1.4',
        fontSize: '14px'
    }
});

// Main extension that combines everything
export function createClaimDependencyHighlighter() {
    return [
        dependenciesState,
        dependencyHighlighter,
        dependencyStyles,
        tooltipStyles,
        tooltipLayer,
        EditorView.domEventHandlers({
            click(event, view) {
                return handleDependencySuggestionClick(view, event);
            }
        })
    ];
}

// Helper function to update dependencies from backend
export function updateClaimDependencies(view, dependencies) {
    console.log('[ClaimDependencyHighlighter] updateClaimDependencies called with:', dependencies);
    view.dispatch({
        effects: setDependencies.of(dependencies)
    });
}

// Helper function to set analysis failed state
export function setClaimAnalysisFailed(view, failed = true) {
    console.log('[ClaimDependencyHighlighter] setClaimAnalysisFailed called with:', failed);
    view.dispatch({
        effects: setAnalysisFailed.of(failed)
    });
}